import React, { useState, useEffect, useRef } from 'react';
import { Navbar } from './components/Navbar';
import { StatusBanner } from './components/StatusBanner';
import { SafeGuardModal } from './components/SafeGuardModal';
import { LiveAuditFeed } from './components/LiveAuditFeed';
import { ToolGrid } from './components/ToolGrid';
import { ShadowStashView } from './components/ShadowStashView';
import { OpenAIBridgeView } from './components/OpenAIBridgeView';
import { SystemHealthView } from './components/SystemHealthView';
import { TunnelStatus, ApprovalTicket, StashEntry, LiveEvent } from './types';
import { Activity, Wrench, History, Bot, Cpu } from 'lucide-react';

export const App: React.FC = () => {
  const [status, setStatus] = useState<TunnelStatus | null>(null);
  const [pendingTickets, setPendingTickets] = useState<ApprovalTicket[]>([]);
  const [stashEntries, setStashEntries] = useState<StashEntry[]>([]);
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const [activeTab, setActiveTab] = useState<'audit' | 'tools' | 'stash' | 'openai' | 'health'>('audit');

  const wsRef = useRef<WebSocket | null>(null);

  // Connect to local IPC server over WebSocket
  useEffect(() => {
    let reconnectTimer: NodeJS.Timeout;

    const getIpcWsUrl = () => {
      if (typeof window === 'undefined') return 'ws://127.0.0.1:19840';
      const isHttps = window.location.protocol === 'https:';
      const proto = isHttps ? 'wss:' : 'ws:';
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return `${proto}//${window.location.hostname}:19840`;
      }
      return `${proto}//${window.location.host}/ipc`;
    };

    const connectIPC = () => {
      try {
        const wsUrl = getIpcWsUrl();
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log(`[IPC] Connected to OpenAITunnel Daemon via ${wsUrl}`);
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            if (data.type === 'INITIAL_STATE') {
              if (data.status) setStatus(data.status);
              if (data.pendingTickets) setPendingTickets(data.pendingTickets);
              if (data.stashBackups) setStashEntries(data.stashBackups);
              if (data.recentEvents) setEvents(data.recentEvents);
            }

            if (data.type === 'EVENT' && data.event) {
              const evt: LiveEvent = data.event;
              setEvents((prev) => [...prev.slice(-200), evt]);

              if (evt.type === 'APPROVAL_REQUESTED') {
                setPendingTickets((prev) => [...prev, evt.payload]);
              }
              if (evt.type === 'APPROVAL_RESOLVED' || evt.type === 'APPROVAL_EXPIRED') {
                setPendingTickets((prev) => prev.filter((t) => t.id !== evt.payload?.ticketId));
              }
              if (evt.type === 'FILE_BACKED_UP') {
                setStashEntries((prev) => [evt.payload, ...prev]);
              }
            }

            if (data.type === 'STATUS_RESPONSE' && data.status) {
              setStatus(data.status);
            }
          } catch (e) {
            console.error('Failed to parse IPC message:', e);
          }
        };

        ws.onclose = () => {
          setStatus((prev) => (prev ? { ...prev, connected: false } : null));
          reconnectTimer = setTimeout(connectIPC, 2500);
        };

        ws.onerror = () => {
          ws.close();
        };
      } catch (e) {
        reconnectTimer = setTimeout(connectIPC, 2500);
      }
    };

    connectIPC();

    return () => {
      clearTimeout(reconnectTimer);
      wsRef.current?.close();
    };
  }, []);

  const handleSetMode = (mode: 'WORK' | 'FULL') => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'SET_MODE', mode }));
      setStatus((prev) => (prev ? { ...prev, mode } : null));
    }
  };

  const handleSetWorkspace = (path: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'SET_WORKSPACE', path }));
      setStatus((prev) => (prev ? { ...prev, activeWorkspace: path } : null));
    }
  };

  const handleApproveTicket = (ticketId: string, decision: 'APPROVE_ONCE' | 'ALLOW_SESSION' | 'REJECT') => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'RESOLVE_APPROVAL', ticketId, decision }));
      setPendingTickets((prev) => prev.filter((t) => t.id !== ticketId));
    }
  };

  const handleRestoreStash = (stashId: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'RESTORE_STASH', stashId }));
    }
  };

  const activeTicket = pendingTickets.length > 0 ? pendingTickets[0] : null;

  return (
    <div className="app-container">
      <Navbar status={status} onSetMode={handleSetMode} onSetWorkspace={handleSetWorkspace} />

      <main className="main-content">
        <StatusBanner
          status={status}
          stashCount={stashEntries.length}
          pendingCount={pendingTickets.length}
        />

        {/* Tab Navigation */}
        <div className="tabs-nav">
          <button
            className={`tab-btn ${activeTab === 'audit' ? 'active' : ''}`}
            onClick={() => setActiveTab('audit')}
          >
            <Activity size={16} />
            Live Audit Stream
          </button>
          <button
            className={`tab-btn ${activeTab === 'tools' ? 'active' : ''}`}
            onClick={() => setActiveTab('tools')}
          >
            <Wrench size={16} />
            MCP Tools Catalog ({status?.totalTools ?? 56})
          </button>
          <button
            className={`tab-btn ${activeTab === 'stash' ? 'active' : ''}`}
            onClick={() => setActiveTab('stash')}
          >
            <History size={16} />
            Shadow Stash Rollbacks ({stashEntries.length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'openai' ? 'active' : ''}`}
            onClick={() => setActiveTab('openai')}
          >
            <Bot size={16} />
            Connect ChatGPT / OpenAI
          </button>
          <button
            className={`tab-btn ${activeTab === 'health' ? 'active' : ''}`}
            onClick={() => setActiveTab('health')}
          >
            <Cpu size={16} />
            System & Diagnostics
          </button>
        </div>

        {/* Tab Contents */}
        {activeTab === 'audit' && (
          <LiveAuditFeed events={events} onClear={() => setEvents([])} />
        )}
        {activeTab === 'tools' && <ToolGrid />}
        {activeTab === 'stash' && (
          <ShadowStashView entries={stashEntries} onRestore={handleRestoreStash} />
        )}
        {activeTab === 'openai' && <OpenAIBridgeView status={status} />}
        {activeTab === 'health' && <SystemHealthView status={status} />}
      </main>

      {/* Human-in-the-Loop SafeGuard Modal */}
      {activeTicket && (
        <SafeGuardModal ticket={activeTicket} onApprove={handleApproveTicket} />
      )}
    </div>
  );
};
